import { motion } from 'framer-motion'
import { Shield, DollarSign, BarChart3, Zap } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useEffect, useState } from 'react'

interface MetricCardProps {
  title: string
  value: string
  change: string
  icon: any
  color: string
  index: number
}

const MetricCard = ({ title, value, change, icon: Icon, color, index }: MetricCardProps) => {
  const [displayValue, setDisplayValue] = useState('0')
  const [isPositive, setIsPositive] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayValue(value)
      setIsPositive(!change.startsWith('-'))
    }, index * 200)
    
    return () => clearTimeout(timer)
  }, [value, change, index])

  const colorClasses = {
    red: 'text-red-400 bg-red-400/10 border-red-400/20',
    green: 'text-green-400 bg-green-400/10 border-green-400/20',
    blue: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    purple: 'text-purple-400 bg-purple-400/10 border-purple-400/20'
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        type: "spring",
        stiffness: 300,
        damping: 25 
      }}
      whileHover={{ scale: 1.02, y: -2 }}
      className="group"
    >
      <Card className="bg-card/50 backdrop-blur-sm border-border hover:border-primary/20 transition-all duration-300">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <div className={`p-2 rounded-lg ${colorClasses[color as keyof typeof colorClasses]} group-hover:scale-110 transition-transform`}>
            <Icon className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground mb-1">
            <motion.span
              key={displayValue}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {displayValue}
            </motion.span>
          </div>
          <motion.p 
            className={`text-xs flex items-center ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 + 0.3 }}
          >
            <span className={`mr-1 ${isPositive ? '↗' : '↘'}`}>
              {isPositive ? '↗' : '↘'}
            </span>
            {change} vs yesterday
          </motion.p>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export const LiveMetrics = () => {
  const metrics = [
    {
      title: "Fraud Blocked Today",
      value: "79",
      change: "+12",
      icon: Shield,
      color: "red"
    },
    {
      title: "Money Saved",
      value: "$2,370",
      change: "+$420",
      icon: DollarSign,
      color: "green"
    },
    {
      title: "Detection Rate",
      value: "95.3%",
      change: "+5.2%",
      icon: BarChart3,
      color: "blue"
    },
    {
      title: "Processing Speed",
      value: "87ms",
      change: "-13ms",
      icon: Zap,
      color: "purple"
    }
  ]

  return (
    <div className="py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Live Performance Metrics
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Real-time insights into fraud prevention performance and business impact
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <MetricCard
            key={metric.title}
            {...metric}
            index={index}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="text-center mt-8"
      >
        <p className="text-sm text-muted-foreground">
          ⚡ Updated every 30 seconds • 📊 Real customer data • 🔒 Privacy protected
        </p>
      </motion.div>
    </div>
  )
}